/**
 * /account/refund-compliance — Vinci Refund Compliance Desk.
 * Watertight buyer flow: pick a fulfilled order → upload 10 mandatory
 * docs → record video witness → accept No-Claims Certificate → verify
 * email + SMS OTP → submit. Every step gated; 10-step vetting timeline
 * shown once submitted.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, FileText, Upload, CheckCircle2, Lock,
  Clock, Send, AlertTriangle, ArrowLeft, Video as VideoIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { VideoWitness } from "../components/VideoWitness";

const STAGE_LABELS = {
  pending: "Pending",
  in_review: "In Review",
  done: "Cleared",
  failed: "Rejected",
};

function fmtInr(n) {
  const v = Number(n || 0);
  return `INR ${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatusPill({ status }) {
  const map = {
    draft:       ["#C9A961", "Draft"],
    submitted:   ["#2FB999", "Submitted"],
    under_review:["#F5D67A", "Under Review"],
    approved:    ["#2FB999", "Approved"],
    rejected:    ["#C0392B", "Rejected"],
  };
  const [c, l] = map[status] || ["#8A8A82", status];
  return <span className="inline-block font-grotesk text-[10px] uppercase tracking-[0.28em] px-3 py-1 rounded-full border" style={{ color: c, borderColor: `${c}55`, background: `${c}10` }}>{l}</span>;
}

// ───────── Section: hero header ──────────
function DeskHeader({ config }) {
  return (
    <div className="rounded-sm border border-[#C0392B]/40 bg-gradient-to-br from-[#1a0d0a] via-[#0f0808] to-[#0a0606] p-7 mb-8" data-testid="compliance-header">
      <div className="flex items-start gap-4">
        <ShieldAlert size={30} className="text-[#C0392B] mt-1 shrink-0" />
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#C0392B]">Refund Compliance Desk</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#F5F5F0] mt-2">A watertight, audited channel — no back-channel refunds</h1>
          <p className="font-body text-sm text-[#C9C9C2]/85 mt-3 max-w-3xl leading-relaxed">
            {config?.audit_notice || "All submissions are subject to a strict 30-day forensic audit period."}
          </p>
          <div className="mt-4 rounded-sm border border-[#C0392B]/50 bg-black/40 px-4 py-3 font-body text-[13px] text-[#F5F5F0] leading-relaxed">
            <b className="text-[#C0392B]">Fraud-Guard notice · </b>
            {config?.penalty_notice}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────── Section: pick an order ──────────
function OrderPicker({ api, onStarted }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [form, setForm] = useState({ reason_category: "quality", reason_summary: "", disputed_amount: "" });

  useEffect(() => {
    (async () => {
      try { setOrders((await api.get("/refund-compliance/eligible-orders")).data.orders || []); }
      catch { toast.error("Could not load your fulfilled orders."); }
      finally { setLoading(false); }
    })();
  }, [api]);

  const initiate = async (o) => {
    if (!form.reason_summary || !form.disputed_amount) { toast.error("Fill in reason and disputed amount."); return; }
    setStarting(o.order_id);
    try {
      const { data } = await api.post("/refund-compliance/initiate", {
        order_id: o.order_id, reason_category: form.reason_category,
        reason_summary: form.reason_summary,
        disputed_amount: Number(form.disputed_amount),
      });
      onStarted(data.claim);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not initiate the claim.");
    } finally { setStarting(null); }
  };

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-black/40 p-6 md:p-8" data-testid="order-picker">
      <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Step 0 · Transaction Hard-Lock</div>
      <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Select the fulfilled order this claim refers to</h2>
      <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
        Refund claims can only be filed against a real, delivered Vinci order.
        Free-text claims are not accepted.
      </p>

      <div className="grid md:grid-cols-3 gap-3 mt-6">
        <div className="md:col-span-1">
          <label className="font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#8A8A82]">Reason category</label>
          <select data-testid="reason-category" value={form.reason_category} onChange={(e) => setForm({ ...form, reason_category: e.target.value })}
            className="w-full mt-1 bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]">
            <option value="short_delivery">Short delivery</option>
            <option value="quality">Quality / spec mismatch</option>
            <option value="billing">Billing error</option>
            <option value="damaged">Damaged in transit</option>
            <option value="non_delivery">Non delivery</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#8A8A82]">Disputed amount (INR)</label>
          <input data-testid="disputed-amount" type="number" step="0.01" min="0" value={form.disputed_amount}
            onChange={(e) => setForm({ ...form, disputed_amount: e.target.value })}
            className="w-full mt-1 bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
        </div>
        <div className="md:col-span-3">
          <label className="font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#8A8A82]">Short reason summary (1-2 sentences)</label>
          <textarea data-testid="reason-summary" rows={2} value={form.reason_summary}
            onChange={(e) => setForm({ ...form, reason_summary: e.target.value })}
            className="w-full mt-1 bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
        </div>
      </div>

      <div className="mt-6">
        {loading ? <div className="font-body text-sm text-[#8A8A82]">Loading fulfilled orders…</div> :
         orders.length === 0 ? (
          <div className="rounded-sm border border-[#C9A961]/15 bg-black/40 p-5 text-center">
            <Lock size={24} className="mx-auto text-[#8A8A82]" />
            <p className="font-body text-sm text-[#8A8A82] mt-2">You have no fulfilled orders — no claim can be raised.</p>
          </div>) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.order_id} data-testid="eligible-order-row" className="flex flex-wrap items-center gap-4 justify-between rounded-sm border border-[#C9A961]/15 hover:border-[#C9A961]/35 bg-black/40 p-4 transition-colors">
                <div>
                  <div className="font-serif text-lg text-[#F5F5F0]">Order #{String(o.order_id).slice(0, 8).toUpperCase()}</div>
                  <div className="font-body text-xs text-[#8A8A82] mt-1">
                    {(o.created_at || "").slice(0, 10)} · {o.items_count} item(s) · {fmtInr(o.total)}
                  </div>
                </div>
                <button data-testid="initiate-claim-btn" disabled={starting === o.order_id} onClick={() => initiate(o)}
                  className="rounded-sm bg-[#C9A961] hover:bg-[#F5D67A] text-black px-5 py-2 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-40 transition-colors">
                  {starting === o.order_id ? "Starting…" : "Initiate claim"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────── Section: document uploader (10 slots) ─────
function DocumentUploader({ api, claim, slots, refresh }) {
  const [uploading, setUploading] = useState(null);
  const done = claim.documents || {};

  const upload = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("slot", slot); fd.append("file", file);
      await api.post(`/refund-compliance/claim/${claim.id}/upload-document`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Uploaded.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(null); }
  };

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-black/40 p-6 md:p-8" data-testid="doc-uploader">
      <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Step 1 · Mandatory Audit Trail</div>
      <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Upload every required document</h2>
      <p className="font-body text-sm text-[#8A8A82] mt-2">
        Each of the {slots.length} slots below must be filled. PDF · JPEG · PNG · WEBP up to 15 MB.
      </p>
      <div className="grid md:grid-cols-2 gap-3 mt-6">
        {slots.map((s, i) => {
          const has = !!done[s.key];
          return (
            <div key={s.key} data-testid={`doc-slot-${s.key}`}
              className={`rounded-sm border p-4 transition-colors ${has ? "border-[#2FB999]/50 bg-[#2FB999]/[0.05]" : "border-[#C9A961]/20 bg-black/40"}`}>
              <div className="flex items-start gap-3">
                <div className={`shrink-0 h-8 w-8 rounded-full grid place-items-center font-grotesk text-[11px] ${has ? "bg-[#2FB999]/20 text-[#2FB999]" : "bg-[#C9A961]/15 text-[#C9A961]"}`}>{has ? <CheckCircle2 size={14} /> : (i + 1)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-body text-sm text-[#F5F5F0] leading-snug">{s.label}</div>
                  {has ? (
                    <div className="font-body text-[11px] text-[#8A8A82] mt-1 truncate">{done[s.key].filename}</div>
                  ) : (
                    <label className="mt-2 inline-block cursor-pointer font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961] hover:text-[#F5D67A] transition-colors">
                      <Upload size={13} className="inline mr-1 -mt-0.5" />{uploading === s.key ? "Uploading…" : "Select file"}
                      <input data-testid={`doc-input-${s.key}`} type="file" className="hidden" accept="application/pdf,image/*"
                        disabled={uploading === s.key} onChange={(e) => upload(s.key, e.target.files?.[0])} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────── Section: video witness ─────
function VideoStep({ api, claim, refresh, user }) {
  const uploaded = !!claim.video_witness;
  const [uploading, setUploading] = useState(false);
  const script = useMemo(() => {
    const biz = user?.profile?.business_name || user?.name || "our company";
    const name = user?.name || user?.email || "the authorised signatory";
    const oid = String(claim.order_id || "").slice(0, 8).toUpperCase();
    return `I am ${name} of ${biz}, and I am initiating this refund claim for Order #${oid} under penalty of permanent expulsion from the Vinci network.`;
  }, [claim.order_id, user]);

  const handleRecorded = async (blob) => {
    if (!blob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "witness.webm");
      await api.post(`/refund-compliance/claim/${claim.id}/upload-video`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Video witness stored.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video upload failed.");
    } finally { setUploading(false); }
  };

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-black/40 p-6 md:p-8" data-testid="video-step">
      <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Step 2 · Identity Witness</div>
      <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Record a live 20-second video statement</h2>
      {uploaded ? (
        <div className="mt-4 rounded-sm border border-[#2FB999]/40 bg-[#2FB999]/[0.05] p-4 flex items-center gap-3">
          <CheckCircle2 size={22} className="text-[#2FB999]" />
          <div>
            <div className="font-body text-sm text-[#F5F5F0]">Witness video captured and secured.</div>
            <div className="font-body text-[11px] text-[#8A8A82]">Recorded at {claim.video_witness.recorded_at?.slice(0, 19)} UTC · IP {claim.video_witness.ip}</div>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <VideoWitness script={script} onRecorded={handleRecorded} disabled={uploading} />
          {uploading && <div className="mt-3 font-body text-[12px] text-[#C9A961]">Uploading witness…</div>}
        </div>
      )}
    </div>
  );
}

// ───────── Section: no-claims certificate ─────
function NoClaimsStep({ api, claim, refresh, user }) {
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [nameConf, setNameConf] = useState(user?.name || "");

  const done = !!claim.no_claims_cert;

  const preview = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/refund-compliance/claim/${claim.id}/no-claims-cert.pdf`;
    window.open(url, "_blank");
  };

  const accept = async () => {
    if (!accepted || !nameConf) { toast.error("Tick the box and enter your name."); return; }
    setBusy(true);
    try {
      await api.post(`/refund-compliance/claim/${claim.id}/accept-no-claims`, { accepted: true, name: nameConf });
      toast.success("No-Claims Certificate accepted.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept."); }
    finally { setBusy(false); }
  };

  const uploadSigned = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/refund-compliance/claim/${claim.id}/upload-no-claims`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Signed No-Claims Certificate uploaded.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-black/40 p-6 md:p-8" data-testid="no-claims-step">
      <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Step 3 · Financial Handshake</div>
      <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Auto-generated No-Claims Certificate</h2>

      {done ? (
        <div className="mt-4 rounded-sm border border-[#2FB999]/40 bg-[#2FB999]/[0.05] p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={22} className="text-[#2FB999]" />
            <div>
              <div className="font-body text-sm text-[#F5F5F0]">
                Certificate {claim.no_claims_cert.mode === "offline_upload" ? "uploaded (offline-signed)" : "accepted digitally"}.
              </div>
              <div className="font-body text-[11px] text-[#8A8A82]">
                Recorded at {claim.no_claims_cert.acknowledgement?.accepted_at?.slice(0, 19)} UTC
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <p className="font-body text-sm text-[#C9C9C2]/85 max-w-2xl">
            Review the auto-generated certificate below. You can either accept it digitally (checkbox +
            typed name) — or, if your organisation's process demands a wet signature, download it, sign,
            and re-upload the signed PDF.
          </p>
          <div className="flex flex-wrap gap-3">
            <button data-testid="preview-no-claims" onClick={preview}
              className="rounded-sm border border-[#C9A961]/50 hover:bg-[#C9A961]/10 text-[#C9A961] px-5 py-2 font-grotesk text-[11px] uppercase tracking-[0.28em] transition-colors">
              <FileText size={13} className="inline mr-2 -mt-0.5" />Preview PDF
            </button>
            <label className="inline-block cursor-pointer">
              <span className="rounded-sm border border-[#C9A961]/40 hover:bg-[#C9A961]/10 text-[#C9A961] px-5 py-2 font-grotesk text-[11px] uppercase tracking-[0.28em] transition-colors inline-block">
                <Upload size={13} className="inline mr-2 -mt-0.5" />Upload signed offline PDF
              </span>
              <input data-testid="offline-noclaims-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadSigned(e.target.files?.[0])} disabled={busy} />
            </label>
          </div>
          <div className="mt-4 rounded-sm border border-[#C9A961]/20 bg-black/40 p-4">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-3">Or accept digitally</div>
            <label className="flex items-start gap-3 mb-3">
              <input data-testid="accept-noclaims" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1" />
              <span className="font-body text-[13px] text-[#F5F5F0] leading-relaxed">
                I confirm the declaration in the certificate is true, and that I hold no other pending disputes
                against Vinci Inc. beyond this specific claim.
              </span>
            </label>
            <input data-testid="accept-name" type="text" value={nameConf} onChange={(e) => setNameConf(e.target.value)}
              placeholder="Full name of authorised signatory"
              className="w-full bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
            <button data-testid="accept-noclaims-btn" onClick={accept} disabled={busy || !accepted || !nameConf}
              className="mt-3 rounded-sm bg-[#C9A961] hover:bg-[#F5D67A] text-black px-5 py-2 font-grotesk text-[11px] uppercase tracking-[0.28em] transition-colors disabled:opacity-40">
              Accept & sign digitally
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────── Section: OTP + submit ─────
function OtpSubmitStep({ api, claim, refresh, config }) {
  const [phone, setPhone] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [smsOtp, setSmsOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [otpState, setOtpState] = useState(null); // { email_sent, sms:{sent, ...} }

  const request = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/refund-compliance/claim/${claim.id}/request-otp`, { phone });
      setOtpState(data);
      toast.success("OTP dispatched.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || (e?.response?.data?.detail?.missing ? "Missing: " + e.response.data.detail.missing.join(", ") : e?.response?.data?.detail)
        || "Could not send OTP.");
    } finally { setSending(false); }
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.post(`/refund-compliance/claim/${claim.id}/submit`, {
        email_otp: emailOtp,
        sms_otp: smsOtp || null,
      });
      toast.success("Claim submitted to Vinci Compliance Desk.");
      setSubmitted(true);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed.");
    } finally { setSending(false); }
  };

  if (claim.status !== "draft") return null;

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-black/40 p-6 md:p-8" data-testid="otp-step">
      <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Step 4 · Multi-Factor Verification</div>
      <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Verify email {config?.sms_enabled ? "+ SMS " : ""}OTP and submit</h2>

      {!otpState ? (
        <div className="mt-5 space-y-3">
          <div>
            <label className="font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#8A8A82]">Mobile number (E.164 or 10-digit IN)</label>
            <input data-testid="otp-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+919812345678"
              className="w-full mt-1 bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
          </div>
          <button data-testid="send-otp-btn" onClick={request} disabled={sending}
            className="rounded-sm bg-[#C9A961] hover:bg-[#F5D67A] text-black px-5 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.28em] transition-colors disabled:opacity-40">
            {sending ? "Dispatching…" : "Send OTP now"}
          </button>
          {!config?.sms_enabled && (
            <div className="text-[11px] font-body text-[#F5D67A]/80">
              SMS OTP is not configured yet — email OTP will be enforced. Add Twilio to activate SMS.
            </div>
          )}
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="rounded-sm border border-[#C9A961]/20 bg-black/40 p-4">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">Email OTP</div>
            <input data-testid="email-otp" type="text" inputMode="numeric" maxLength={6} value={emailOtp}
              onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))}
              className="mt-2 w-40 bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-mono text-lg tracking-[6px] text-[#F5F5F0]" />
          </div>
          {otpState.sms?.sent && (
            <div className="rounded-sm border border-[#C9A961]/20 bg-black/40 p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">SMS OTP · {otpState.sms.to}</div>
              <input data-testid="sms-otp" type="text" inputMode="numeric" maxLength={6} value={smsOtp}
                onChange={(e) => setSmsOtp(e.target.value.replace(/\D/g, ""))}
                className="mt-2 w-40 bg-black border border-[#C9A961]/30 rounded-sm px-3 py-2 font-mono text-lg tracking-[6px] text-[#F5F5F0]" />
            </div>
          )}
          {!otpState.sms?.sent && otpState.sms?.reason && otpState.sms.reason !== "not_configured" && (
            <div className="text-[12px] font-body text-[#F5D67A]/80">
              SMS could not be sent ({otpState.sms.reason}). Email OTP will suffice.
            </div>
          )}
          <button data-testid="submit-claim-btn" onClick={submit} disabled={sending || submitted}
            className="rounded-sm bg-[#2FB999] hover:bg-[#3ECEB0] text-black px-6 py-3 font-grotesk text-[11px] uppercase tracking-[0.32em] transition-colors disabled:opacity-40">
            <Send size={13} className="inline mr-2 -mt-0.5" />
            {sending ? "Submitting…" : "Sign & Submit Claim"}
          </button>
        </div>
      )}
    </div>
  );
}

// ───────── Section: post-submission timeline ─────
function Timeline({ steps }) {
  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-black/40 p-6 md:p-8" data-testid="vetting-timeline">
      <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Vetting Timeline</div>
      <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">10-step forensic review — live</h2>
      <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
        Every step is stamped by the Vinci Compliance Desk with an auditable timestamp.
        No follow-up calls needed — this timeline is the source of truth.
      </p>
      <ol className="mt-6 space-y-3">
        {(steps || []).map((s, i) => {
          const c = s.status === "done" ? "#2FB999"
                  : s.status === "failed" ? "#C0392B"
                  : s.status === "in_review" ? "#F5D67A" : "#8A8A82";
          return (
            <li key={s.key} data-testid={`step-${s.key}`} className="flex items-start gap-4 rounded-sm border border-[#C9A961]/10 bg-black/40 p-4">
              <div className="shrink-0 h-8 w-8 rounded-full grid place-items-center font-grotesk text-[11px] tabular-nums"
                style={{ background: `${c}22`, color: c }}>{s.status === "done" ? <CheckCircle2 size={14} /> : (i + 1)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-body text-[15px] text-[#F5F5F0]">{s.label}</div>
                <div className="font-body text-[11px] text-[#8A8A82] mt-0.5">
                  <span style={{ color: c }}>{STAGE_LABELS[s.status] || s.status}</span>
                  {s.completed_at ? ` · ${s.completed_at.slice(0, 19)} UTC` : ""}
                  {s.note ? ` · ${s.note}` : ""}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ───────── Main page ─────
export default function RefundComplianceDesk() {
  const { user, api } = useAuth();
  const [config, setConfig] = useState(null);
  const [claim, setClaim] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, my] = await Promise.all([
        api.get("/refund-compliance/config"),
        api.get("/refund-compliance/my-claims"),
      ]);
      setConfig(cfg.data);
      setClaims(my.data.claims || []);
      const draft = (my.data.claims || []).find((c) => c.status === "draft");
      setClaim(draft || null);
    } catch (e) {
      toast.error("Could not load the Compliance Desk.");
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const refreshClaim = async () => {
    if (!claim) { load(); return; }
    try {
      const { data } = await api.get(`/refund-compliance/claim/${claim.id}`);
      setClaim(data.claim);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] grain grid place-items-center">
        <div className="w-12 h-12 rounded-full border-2 border-[#C9A961]/30 border-t-[#C9A961] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] grain">
      <Toaster theme="dark" position="top-center" />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Link to="/account" className="inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A8A82] hover:text-[#C9A961] transition-colors mb-6" data-testid="back-to-account">
          <ArrowLeft size={12} />Back to Account
        </Link>
        <DeskHeader config={config} />

        {claim ? (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#8A8A82]">Active claim</div>
                <div className="font-serif text-xl text-[#F5F5F0] mt-0.5">#{claim.id.slice(0, 8).toUpperCase()} · Order #{String(claim.order_id).slice(0, 8).toUpperCase()}</div>
              </div>
              <StatusPill status={claim.status} />
            </div>

            {claim.status === "draft" && (
              <>
                <DocumentUploader api={api} claim={claim} slots={config?.doc_slots || []} refresh={refreshClaim} />
                <VideoStep api={api} claim={claim} refresh={refreshClaim} user={user} />
                <NoClaimsStep api={api} claim={claim} refresh={refreshClaim} user={user} />
                <OtpSubmitStep api={api} claim={claim} refresh={refreshClaim} config={config} />
              </>
            )}

            {claim.status !== "draft" && <Timeline steps={claim.timeline} />}
          </div>
        ) : (
          <>
            <OrderPicker api={api} onStarted={(c) => { setClaim(c); setClaims([c, ...claims]); }} />
            {claims.length > 0 && (
              <div className="mt-10">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961] mb-3">Past claims</div>
                <div className="space-y-2">
                  {claims.map((c) => (
                    <button key={c.id} onClick={() => setClaim(c)} data-testid={`past-claim-${c.id}`}
                      className="w-full text-left flex flex-wrap items-center justify-between gap-3 rounded-sm border border-[#C9A961]/15 hover:border-[#C9A961]/35 bg-black/40 p-4 transition-colors">
                      <div>
                        <div className="font-serif text-[#F5F5F0]">#{c.id.slice(0, 8).toUpperCase()} · Order #{String(c.order_id).slice(0, 8).toUpperCase()}</div>
                        <div className="font-body text-[11px] text-[#8A8A82] mt-1">
                          {c.created_at?.slice(0, 10)} · {fmtInr(c.disputed_amount)}
                        </div>
                      </div>
                      <StatusPill status={c.status} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
